import { gapi } from 'gapi-script';

// These will be provided by you
const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const API_KEY = import.meta.env.VITE_GOOGLE_API_KEY || '';

const SCOPES = [
  'https://www.googleapis.com/auth/gmail.send',
  'https://www.googleapis.com/auth/gmail.compose'
];

let gapiInitialized = false;

export async function initGapi(): Promise<void> {
  if (gapiInitialized) return;

  try {
    await new Promise<void>((resolve, reject) => {
      gapi.load('client:auth2', async () => {
        try {
          await gapi.client.init({
            apiKey: API_KEY,
            clientId: CLIENT_ID,
            scope: SCOPES.join(' '),
            discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/gmail/v1/rest']
          });
          gapiInitialized = true;
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error('Error initializing GAPI:', error);
    throw error;
  }
}

// Check if Gmail is authenticated
export function isGmailAuthenticated(): boolean {
  if (!gapiInitialized) return false;
  return gapi.auth2?.getAuthInstance()?.isSignedIn.get() || false;
}

// Get authentication URL
export async function getAuthUrl(): Promise<string> {
  if (!gapiInitialized) await initGapi();
  
  const auth2 = gapi.auth2.getAuthInstance();
  const authUrl = await auth2.grantOfflineAccess().then((authResult) => {
    if (authResult['code']) {
      return `/auth/google/callback?code=${authResult['code']}`;
    }
    throw new Error('Failed to get authorization code');
  });

  return authUrl;
}

// Handle authentication callback
export async function handleAuthCallback(code: string): Promise<void> {
  if (!gapiInitialized) await initGapi();

  try {
    const auth2 = gapi.auth2.getAuthInstance();
    await auth2.signIn();
  } catch (error) {
    console.error('Error handling Gmail auth callback:', error);
    throw error;
  }
}

// Revoke Gmail access
export async function revokeGmailAccess(): Promise<void> {
  if (!gapiInitialized) return;

  try {
    const auth2 = gapi.auth2.getAuthInstance();
    if (auth2.isSignedIn.get()) {
      await auth2.signOut();
      await auth2.disconnect();
    }
  } catch (error) {
    console.error('Error revoking Gmail access:', error);
    throw error;
  }
}

// Send email
export async function sendEmail(to: string, subject: string, body: string): Promise<boolean> {
  if (!gapiInitialized) await initGapi();

  try {
    if (!isGmailAuthenticated()) {
      throw new Error('Not authenticated with Gmail');
    }

    // Create email message
    const message = [
      'Content-Type: text/plain; charset="UTF-8"\n',
      'MIME-Version: 1.0\n',
      'Content-Transfer-Encoding: 7bit\n',
      `To: ${to}\n`,
      `Subject: ${subject}\n\n`,
      body
    ].join('');

    const encodedMessage = btoa(message)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    // Send email using Gmail API
    await gapi.client.gmail.users.messages.send({
      userId: 'me',
      resource: {
        raw: encodedMessage
      }
    });

    return true;
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
}